import { NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import type { Params } from '@angular/router';
import { AppLinkService } from '../../../core/services';
import { ShortcutsService } from '../shortcuts/shortcuts.service';
import { SidebarService } from '../sidebar.service';
import { SidebarItemDefinition } from './sidebar-item.model';

interface LabelSegment {
    text: string;
    isMatch: boolean;
}

@Component({
    selector: 'app-sidebar-item',
    imports: [NgClass, NgTemplateOutlet, RouterLink],
    templateUrl: './sidebar-item.component.html',
    styleUrls: ['./sidebar-item.component.css', '../sidebar-interaction.css']
})
export class SidebarItemComponent {
    sidebarItem = input.required<SidebarItemDefinition>();

    isRoot = input(false);

    private appLinkService = inject(AppLinkService);
    private shortcutsService = inject(ShortcutsService);
    private router = inject(Router);

    sidebarService = inject(SidebarService);

    isExpanded = signal(false);

    // Folders stay open; only root domains collapse.
    isChildrenExpanded = computed(() => !this.isRoot() || this.sidebarService.isSearching() || this.isExpanded());

    isActiveSearchResult = computed(() => this.sidebarService.isActiveSearchResult(this.sidebarItem()));

    isSearching = this.sidebarService.isSearching;

    isPlaceholder = computed(() => !!this.sidebarItem().isPlaceholder);

    isRouteActive = computed(() => {
        this.sidebarService.currentUrl();

        const routerLink = this.sidebarItem().routerLink;

        if (!routerLink || this.isPlaceholder()) {
            return false;
        }

        return this.isRouteActiveLink(routerLink);
    });

    isHighlighted = computed(() => {
        if (this.isActiveSearchResult()) {
            return true;
        }

        return this.isRouteActive() && !this.isSearching();
    });

    /** Label split into plain/matched runs so search can bold the matched substrings. */
    labelSegments = computed<LabelSegment[]>(() => {
        const label = this.sidebarItem().label;
        const searchTerms = this.sidebarService.searchTerms();

        if (searchTerms.length === 0) {
            return [{ text: label, isMatch: false }];
        }

        return this.buildLabelSegments(label, searchTerms);
    });

    isPinnable = computed(() => {
        const item = this.sidebarItem();
        return this.shortcutsService.canPin() && this.shortcutsService.isShortcutEligible(item);
    });

    isPinned = computed(() => {
        const routerLink = this.sidebarItem().routerLink;
        return !!routerLink && this.shortcutsService.isPinned(routerLink);
    });

    isPinDisabled = computed(() => !this.isPinned() && this.shortcutsService.isPinnedAtCap());

    pinTitle = computed(() => this.shortcutsService.getPinActionTitle(this.isPinned()));

    constructor() {
        // Each drawer open starts from a clean tree: only the active route's root expanded.
        // Track ONLY the open signal — the item input is read untracked so search filtering and
        // userContext refreshes (which rebuild the tree with new item refs) don't wipe manual
        // expansions while the drawer stays open.
        effect(() => {
            if (!this.sidebarService.isSidebarOpen()) {
                return;
            }

            untracked(() => {
                if (this.isRoot() && this.sidebarItem().items?.length) {
                    this.updateExpandedFromRoute();
                }
            });
        });
    }

    routerLinkQueryParams(): Params | null {
        return this.appLinkService.addImpersonationQueryParams();
    }

    onClick() {
        /* Root parent -> Expand, but don't follow the Router Link */
        if (this.isRoot() && this.sidebarItem().items?.length) {
            this.isExpanded.update((current) => !current);
        }

        /* RouterLink / External Link -> Close the side bar (closing also clears any search) */
        if (this.sidebarItem().routerLink || this.sidebarItem().url) {
            this.sidebarService.closeSidebar();
        }
    }

    onTogglePinned(event: Event) {
        // The pin button lives inside the navigation anchor — don't navigate.
        event.preventDefault();
        event.stopPropagation();

        const routerLink = this.sidebarItem().routerLink;

        if (routerLink) {
            this.shortcutsService.togglePinned(routerLink);
        }
    }

    private updateExpandedFromRoute() {
        this.isExpanded.set(this.hasActiveRoute(this.sidebarItem()));
    }

    private hasActiveRoute(item: SidebarItemDefinition): boolean {
        if (item.routerLink && !item.isPlaceholder && this.isRouteActiveLink(item.routerLink)) {
            return true;
        }

        return item.items?.some((child) => this.hasActiveRoute(child)) ?? false;
    }

    private isRouteActiveLink(routerLink: string): boolean {
        return this.router.isActive(routerLink, {
            paths: 'exact',
            matrixParams: 'ignored',
            queryParams: 'ignored',
            fragment: 'ignored'
        });
    }

    private buildLabelSegments(label: string, searchTerms: string[]): LabelSegment[] {
        const comparableLabel = label.toLocaleLowerCase();
        const isMatchedCharacter = new Array<boolean>(label.length).fill(false);

        for (const term of searchTerms) {
            let searchFrom = 0;

            while (searchFrom <= comparableLabel.length - term.length) {
                const matchIndex = comparableLabel.indexOf(term, searchFrom);

                if (matchIndex === -1) {
                    break;
                }

                isMatchedCharacter.fill(true, matchIndex, matchIndex + term.length);
                searchFrom = matchIndex + 1;
            }
        }

        const segments: LabelSegment[] = [];

        for (let index = 0; index < label.length; index++) {
            const previousSegment = segments[segments.length - 1];

            if (previousSegment && previousSegment.isMatch === isMatchedCharacter[index]) {
                previousSegment.text += label[index];
            } else {
                segments.push({ text: label[index], isMatch: isMatchedCharacter[index] });
            }
        }

        return segments.length > 0 ? segments : [{ text: label, isMatch: false }];
    }
}
